import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { CaseEnvChangeRedirectService } from '@modules/common/services/case-env-change-redirect.service';
import { CurrentRouteService } from "@modules/common/services/current-route.service";
import { ReplaceRouteParameterService } from '@modules/common/services/replace-route-parameter.service';
import { BehaviorSubject, Observable } from "rxjs";
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CurrentEnvironmentService {
    constructor(
        private _router: Router,
        private _currentRouteService: CurrentRouteService,
        private _replaceRouteParameterService: ReplaceRouteParameterService,
        private _caseEnvChangeRedirectService: CaseEnvChangeRedirectService
    ) {
        this._router
            .events
            .pipe(filter(e => e instanceof NavigationEnd))
            .subscribe(() => this.onNavigation());
    }

    private _environmentChangedSubject?: BehaviorSubject<string>;

    private get environmentChangedSubject(): BehaviorSubject<string> {
        //Lazily populate this subject here instead of in the constructor because the current route is not available
        //during app startup, when this service is first constructed
        if (!this._environmentChangedSubject) {
            this._environmentChangedSubject = new BehaviorSubject<string>(this.getCurrentEnvironmentName());
        }

        return this._environmentChangedSubject;
    }

    public getCurrentEnvironmentName(): string {
        return this._currentRouteService.getCurrentRoute().params['environment'] || '';
    }

    public setCurrentEnvironment(newEnvironmentName: string | undefined | null): void {
        this._router.navigate(this.getUpdatedUrl(newEnvironmentName || ''));
    }

    public get environmentChanged(): Observable<string> {
        return this.environmentChangedSubject;
    }

    private onNavigation(): void {
        const newEnvironmentName = this.getCurrentEnvironmentName();
        if (newEnvironmentName != this.environmentChangedSubject.getValue()) {
            this.environmentChangedSubject.next(newEnvironmentName);
        }
    }

    private getUpdatedUrl(newEnvironmentName: string): string[] {
        const currentRoute = this._currentRouteService.getCurrentRoute();

        if (currentRoute.data && currentRoute.data.caseEnvChangeRedirect) {
            return this._caseEnvChangeRedirectService.getCaseEnvChangeRedirectUrl(currentRoute.data.caseEnvChangeRedirect, currentRoute.params['clientName'] || '', newEnvironmentName);
        }

        const result = this._replaceRouteParameterService.replaceRouteParameter({
            route: this._currentRouteService.getCurrentRoute(),
            paramName: 'environment',
            paramValue: newEnvironmentName,
            leaveOutEmptyParameter: true
        });

        if (!this.getCurrentEnvironmentName() && newEnvironmentName) {
            result.splice(0, 0, newEnvironmentName);
        }

        return result;
    }
}
